import { createContext, useContext, useEffect, useState, useCallback, createElement } from "react";
import { toast } from "sonner";
import { api, tokens } from "@/lib/api";

const AuthCtx = createContext(null);
const IDLE_MS = 15 * 60 * 1000;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);

  const logout = useCallback(async (silent) => {
    if (tokens.access) {
      try { await api.post("/auth/logout"); } catch { /* session already ended */ }
    }
    tokens.clear();
    setUser(false);
    if (silent === "idle") toast.info("You were signed out after 15 minutes of inactivity.");
  }, []);

  useEffect(() => {
    if (!tokens.access) { setUser(false); return; }
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => { tokens.clear(); setUser(false); });
  }, []);

  useEffect(() => {
    const onLogout = () => setUser(false);
    window.addEventListener("mc-logout", onLogout);
    return () => { window.removeEventListener("mc-logout", onLogout); };
  }, []);

  useEffect(() => {
    if (!user) return undefined;
    let timer = setTimeout(() => logout("idle"), IDLE_MS);
    const reset = () => { clearTimeout(timer); timer = setTimeout(() => logout("idle"), IDLE_MS); };
    const evts = ["mousedown", "keydown", "touchstart", "scroll"];
    evts.forEach((e) => window.addEventListener(e, reset, { passive: true }));
    return () => { clearTimeout(timer); evts.forEach((e) => window.removeEventListener(e, reset)); };
  }, [user, logout]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    tokens.set(data.access_token, data.refresh_token);
    setUser(data.user);
    return data.user;
  };

  const refreshMe = async () => { const r = await api.get("/auth/me"); setUser(r.data); };
  const can = (p) => !!user && user.permissions?.includes(p);

  return createElement(AuthCtx.Provider, { value: { user, login, logout, can, refreshMe } }, children);
}

export const useAuth = () => useContext(AuthCtx);
